import React from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import fundUpdatesImg from 'src/assets/explore/fund-updates.png'
import memberInsightsImg from 'src/assets/explore/member-insights.png'
import portfolioNewsImg from 'src/assets/explore/portfolio-news.png'

import {
  Typography,
  TypographyVariant,
  TypographyWeight,
} from 'src/components/atoms'

interface IExploreCardProps {
  title: string
  description: string
  image: string
  to: string
}

const ExploreCard = ({
  title,
  description,
  image,
  to,
}: IExploreCardProps): JSX.Element => (
  <Link
    to={to}
    className="group flex flex-col overflow-hidden rounded-[16px] outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-border-strong"
  >
    <div className="relative aspect-[371/88] w-full shrink-0 overflow-hidden">
      <img src={image} alt={title} className="h-full w-full object-cover" />
    </div>
    <div className="flex flex-col gap-1 bg-surface-muted px-3 pb-4 pt-3 transition-colors duration-300 group-hover:bg-content-primary">
      <Typography
        variant={TypographyVariant.titleXs}
        weight={TypographyWeight.light}
        className="text-content-primary transition-colors duration-300 group-hover:text-surface-raised"
      >
        {title}
      </Typography>
      <Typography
        variant={TypographyVariant.bodyXs}
        className="leading-[1.45] text-content-secondary transition-colors duration-300 group-hover:text-content-tertiary"
      >
        {description}
      </Typography>
    </div>
  </Link>
)

export const ExploreCards = (): JSX.Element => {
  const { t } = useTranslation()

  const cards = [
    {
      title: t('overview.explore.portfolioNews.title'),
      description: t('overview.explore.portfolioNews.description'),
      image: portfolioNewsImg,
      to: '/portfolio-news',
    },
    {
      title: t('overview.explore.memberInsights.title'),
      description: t('overview.explore.memberInsights.description'),
      image: memberInsightsImg,
      to: '/member-insights',
    },
    {
      title: t('overview.explore.fundUpdates.title'),
      description: t('overview.explore.fundUpdates.description'),
      image: fundUpdatesImg,
      to: '/fund-updates',
    },
  ]

  return (
    <section className="flex flex-col gap-6">
      <Typography
        variant={TypographyVariant.titleSm}
        weight={TypographyWeight.light}
        className="text-content-primary"
      >
        {t('overview.explore.sectionTitle')}
      </Typography>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {cards.map(card => (
          <ExploreCard key={card.to} {...card} />
        ))}
      </div>
    </section>
  )
}
